import React, { useState, useMemo } from 'react';
import { FormControl, Box, OutlinedInput, Select, MenuItem, Chip, Typography } from '@mui/material';
import { useAllCategory } from '../../Query/CategoryQuery';

interface CategoryIdFieldProps {
  field?: any;
  form?: any;
  storeId: any;
}

const CategoryIdField: React.FC<CategoryIdFieldProps> = ({ field, form, storeId }) => {
  const [open, setOpen] = useState(false);
  // Fetch categories based on store type
  const storeType = storeId?.restaurantDetails?.storeType || 'Restaurant';
  const { data: categoryData } = useAllCategory({
    initialData: {
      storeType,
    },
  });
  const categoryOptions = useMemo(
    () =>
      (categoryData || []).map((item: any) => ({
        value: item.id,
        label: item.nameEnglish,
      })),
    [categoryData]
  );

  React.useEffect(() => {
    if (storeId?.restaurantDetails?.categoryId && form && field) {
      const catIds = Array.isArray(storeId.restaurantDetails.categoryId)
        ? storeId.restaurantDetails.categoryId
        : [storeId.restaurantDetails.categoryId];
      // Only update if different
      if (JSON.stringify(form.values?.[field.name]) !== JSON.stringify(catIds)) {
        form.setFieldValue(field.name, catIds);
      }
    }
    // Only depend on storeId and field.name, NOT form.values[field.name]
  }, [storeId, field?.name]);

  const selectedCategoryIds: string[] = Array.isArray(field.value)
    ? field.value
    : field.value
    ? [field.value]
    : [];

  const handleSelectChange = (event: any) => {
    const { value } = event.target;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    form.setFieldTouched(field.name, true, true);
    form.setFieldValue(field.name, newValue, true);
    setOpen(false);
  };

  const handleChipDelete = (categoryIdToDelete: string) => () => {
    const newSelectedCategoryIds = selectedCategoryIds.filter(
      (categoryId) => categoryId !== categoryIdToDelete
    );
    form.setFieldValue(field.name, newSelectedCategoryIds, true);
    form.setFieldTouched(field.name, true, true);
  };

  const selectedCategories = (categoryOptions || []).filter((option: any) =>
    selectedCategoryIds.includes(option.value)
  );

  // Show error if no category is selected, regardless of touched state
  const hasNoCategorySelected = selectedCategoryIds.length === 0;
  const hasError = (form?.touched?.[field.name] && Boolean(form?.errors?.[field.name])) || hasNoCategorySelected;

  return (
    <FormControl error={hasError} fullWidth sx={{ width: '401px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Select
          multiple
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedCategoryIds}
          onChange={handleSelectChange}
          input={<OutlinedInput />}
          displayEmpty
          renderValue={(selected) => {
            return selected.length === 0 ? (
              <span style={{ color: hasError ? '#d32f2f' : 'rgba(0, 0, 0, 0.38)' }}>
                Select Category
              </span>
            ) : (
              ''
            );
          }}
          sx={{ width: '401px' }}
        >
          <MenuItem value="" disabled>
            <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
              -- Select --
            </span>
          </MenuItem>
          {(categoryOptions || []).map((option: any) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {/* Chips shown in a new row below the select */}
        {selectedCategories.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mt: 1,
            }}
          >
            {selectedCategories.map((category: any) => (
              <Chip
                key={category.value}
                label={category.label}
                onDelete={handleChipDelete(category.value)}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>
        )}
        {/* Error display */}
        {hasError && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {form.errors[field.name] ? (form.errors[field.name] as string) : 'Please select at least one category.'}
          </Typography>
        )}
      </Box>
    </FormControl>
  );
};

export default React.memo(CategoryIdField); 